import {Button} from '@dropbox/dig-components/buttons';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {type AdminTimeframe, type ProductRoadmapResponse, ProductRoadmapService} from 'client';
import {t} from 'i18next';
import {getService} from 'utilities';

export type TimeframeRoadmapDraft = {title: string; url: string};

/** Undefined means unchanged; null means the saved roadmap was removed. */
export const saveTimeframeRoadmap = async (
  timeframe: AdminTimeframe,
  draft: TimeframeRoadmapDraft | null
): Promise<ProductRoadmapResponse | null | undefined> => {
  const roadmap = timeframe.product_roadmap;
  if (draft === null) {
    if (!roadmap) return undefined;
    await getService(ProductRoadmapService).deleteProductRoadmapApiV1ProductRoadmapRoadmapIdDelete(
      roadmap.id
    );
    return null;
  }

  const document = {title: draft.title.trim(), url: draft.url.trim()};
  if (document.title === (roadmap?.title ?? '') && document.url === (roadmap?.url ?? '')) {
    return undefined;
  }

  const roadmapService = getService(ProductRoadmapService);
  return roadmap
    ? roadmapService.editProductRoadmapApiV1ProductRoadmapRoadmapIdPatch(roadmap.id, document)
    : roadmapService.createProductRoadmapApiV1ProductRoadmapPost({
        timeframe_id: timeframe.id,
        ...document,
      });
};

export const isTimeframeRoadmapValid = ({title, url}: TimeframeRoadmapDraft) => {
  if (!title.trim() && !url.trim()) return true;
  if (!title.trim()) return false;
  try {
    const parsed = new URL(url.trim());
    return ['http:', 'https:'].includes(parsed.protocol) && !parsed.username && !parsed.password;
  } catch {
    return false;
  }
};

export const AdminTimeframeRoadmapEditor = ({
  value,
  onChange,
  disabled,
  hasExistingRoadmap,
}: {
  value: TimeframeRoadmapDraft;
  onChange: (value: TimeframeRoadmapDraft) => void;
  disabled: boolean;
  hasExistingRoadmap: boolean;
}) => (
  <Box paddingTop="16" borderTop="Solid" borderColor="Border Subtle">
    <Stack gap="8">
      <Text isBold>{t('admin_timeframe_roadmap_heading')}</Text>
      <TextInput
        aria-label={t('admin_timeframe_roadmap_title')}
        placeholder={t('admin_timeframe_roadmap_title')}
        value={value.title}
        disabled={disabled}
        onChange={(event) => onChange({...value, title: event.target.value})}
      />
      <TextInput
        aria-label={t('admin_timeframe_roadmap_url')}
        placeholder="https://"
        value={value.url}
        disabled={disabled}
        onChange={(event) => onChange({...value, url: event.target.value})}
      />
      {hasExistingRoadmap && (value.title || value.url) && (
        <Box>
          <Button
            size="small"
            variant="borderless"
            disabled={disabled}
            onClick={() => onChange({title: '', url: ''})}
          >
            {t('admin_timeframe_roadmap_remove')}
          </Button>
        </Box>
      )}
      {hasExistingRoadmap && !value.title.trim() && !value.url.trim() && (
        <Text size="small" className={atoms({color: 'Warning Base'})} role="status">
          {t('admin_timeframe_roadmap_remove_pending')}
        </Text>
      )}
    </Stack>
  </Box>
);
